/**
 * Projects → Forecast summary: the Budget Forecast rolled up over the portfolio.
 *
 * The server sends one row per project — totals plus a sparse monthly series in
 * EUR (see `projects/forecast_summary.py`). Everything on screen above the table
 * is derived from the rows the filters left visible, so the indicator strip and
 * the cash-flow chart always describe exactly the projects listed underneath;
 * filtering never needs a round trip.
 *
 * The arithmetic mirrors the project sheet's own cash-flow block
 * (`budgetForecast.js`) — cumulative position, peak funding need, break-even —
 * so a figure here and the same figure one click deeper agree.
 */

import Chart from 'chart.js/auto';

import { DataTable } from '../common/components/DataTable.js';

/** EUR with thousands separators and no cents — these are budget figures. */
const EUR = new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
});

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Row values reach the DOM through innerHTML formatters — escape them. */
function esc(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
}

function fmtEur(value) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? EUR.format(n) : '—';
}

/** "Mar 2026" from a "2026-03" key. */
function monthLabel(key) {
    const [y, m] = String(key || '').split('-');
    if (!y || !m) return key || '—';
    return `${MONTHS[+m - 1]} ${y}`;
}

/** Sum a numeric field over rows, ignoring the rows that have no value. */
function sumField(rows, field) {
    return rows.reduce((acc, r) => acc + (Number.isFinite(r[field]) ? r[field] : 0), 0);
}

export function initForecastSummary() {
    const holder = document.getElementById('forecastSummaryData');
    if (!holder || !document.getElementById('forecastSummaryTable')) return;

    const payload = JSON.parse(holder.textContent);
    const allMonths = payload.months || [];
    const withActuals = !!payload.with_actuals;

    const kpiWrap = document.getElementById('fs-kpis');
    const caveatBox = document.getElementById('fs-caveats');
    const caveatText = document.getElementById('fs-caveats-text');
    const scopeLabel = document.getElementById('fs-scope');
    const chartCanvas = document.getElementById('fs-chart');
    const chartEmpty = document.getElementById('fs-chart-empty');
    let chart = null;

    // --- indicators ---------------------------------------------------------

    function kpi(label, value, note, tone) {
        const box = document.createElement('div');
        box.className = `fs-kpi${tone ? ` is-${tone}` : ''}`;
        const l = document.createElement('span');
        l.className = 'fs-kpi-label';
        l.textContent = label;
        const v = document.createElement('span');
        v.className = 'fs-kpi-value';
        v.textContent = value;
        box.append(l, v);
        if (note) {
            const n = document.createElement('span');
            n.className = 'fs-kpi-note';
            n.textContent = note;
            box.appendChild(n);
        }
        return box;
    }

    /**
     * The three monthly series summed over `rows`, plus the cumulative position.
     * Only months that actually carry money are kept, so a filter down to one
     * project shortens the axis instead of drawing years of empty bars.
     */
    function series(rows) {
        const staff = {};
        const other = {};
        const revenue = {};
        rows.forEach((row) => {
            const s = row.series || {};
            Object.entries(s.staff_cost || {}).forEach(([m, v]) => { staff[m] = (staff[m] || 0) + v; });
            Object.entries(s.other_cost || {}).forEach(([m, v]) => { other[m] = (other[m] || 0) + v; });
            Object.entries(s.revenue || {}).forEach(([m, v]) => { revenue[m] = (revenue[m] || 0) + v; });
        });
        const months = allMonths.filter((m) => staff[m] || other[m] || revenue[m]);
        let running = 0;
        const cumulative = months.map((m) => {
            running += (revenue[m] || 0) - (staff[m] || 0) - (other[m] || 0);
            return running;
        });
        return { months, staff, other, revenue, cumulative };
    }

    function renderIndicators(rows, computed) {
        kpiWrap.innerHTML = '';

        const budget = sumField(rows, 'budget');
        const staffCost = sumField(rows, 'staff_cost');
        const otherCost = sumField(rows, 'other_cost');
        const totalCost = sumField(rows, 'total_cost');
        const revenue = sumField(rows, 'revenue');
        // Projects with no budget on file would drag the margin down as pure
        // cost, so the margin is stated over the projects that have one and the
        // rest are called out beside it.
        const budgeted = rows.filter((r) => r.has_budget);
        const budgetedCost = sumField(budgeted, 'total_cost');
        const margin = budget - budgetedCost;
        const unbudgeted = rows.length - budgeted.length;

        kpiWrap.appendChild(kpi(
            'Projects', String(rows.length),
            unbudgeted ? `${unbudgeted} with no budget on file` : 'All carry a budget',
            unbudgeted ? 'bad' : null,
        ));
        kpiWrap.appendChild(kpi(
            'Budget', fmtEur(budget), 'Contract value, converted',
        ));
        kpiWrap.appendChild(kpi(
            withActuals ? 'Cost to date + forecast' : 'Forecast cost',
            fmtEur(totalCost),
            `${fmtEur(staffCost)} staff · ${fmtEur(otherCost)} other`,
        ));
        // What a staffed day costs across these projects, weighted by the days
        // themselves — the staffing mix of the portfolio in one figure.
        const days = sumField(rows, 'staff_days');
        kpiWrap.appendChild(kpi(
            'Avg internal day rate',
            days ? fmtEur(staffCost / days) : '—',
            days ? `Over ${days.toLocaleString('en-GB')} priced day(s)` : 'No days with a day rate yet',
        ));
        kpiWrap.appendChild(kpi(
            withActuals ? 'Margin at completion' : 'Gross margin',
            fmtEur(margin),
            budget ? `${(margin / budget * 100).toFixed(1)}% of budget` : 'No budget to compare',
            margin < 0 ? 'bad' : 'good',
        ));
        const gap = budget - revenue;
        kpiWrap.appendChild(kpi(
            'Scheduled revenue', fmtEur(revenue),
            Math.abs(gap) >= 1
                ? `${fmtEur(Math.abs(gap))} ${gap > 0 ? 'not yet scheduled' : 'over budget'}`
                : 'Matches the budget',
            Math.abs(gap) >= 1 ? 'bad' : null,
        ));

        // CAPUR: revenue less the pass-through spend, i.e. what is left to cover
        // staff and carry the margin.
        const capur = revenue - otherCost;
        kpiWrap.appendChild(kpi(
            'CAPUR', fmtEur(capur),
            `Scheduled revenue less ${fmtEur(otherCost)} of other costs`,
        ));
        // The other side of the same day: what the client pays for it. Over
        // *every* man-day, priced or not — CAPUR does not depend on day rates.
        const manDays = sumField(rows, 'man_days');
        kpiWrap.appendChild(kpi(
            'Ext avg day rate',
            manDays ? fmtEur(capur / manDays) : '—',
            manDays
                ? `CAPUR over ${manDays.toLocaleString('en-GB')} man-day(s)`
                : 'No days forecast yet',
        ));

        // Peak funding need: the deepest the cumulative position goes below
        // zero — the working capital the portfolio has to carry.
        let trough = 0;
        let troughMonth = null;
        computed.cumulative.forEach((value, i) => {
            if (value < trough) { trough = value; troughMonth = computed.months[i]; }
        });
        kpiWrap.appendChild(kpi(
            'Peak funding need', fmtEur(-trough),
            troughMonth ? `Deepest at ${monthLabel(troughMonth)}` : 'Never cash-negative',
            trough < 0 ? 'bad' : 'good',
        ));
    }

    /** Say plainly what the roll-up could not account for, rather than flatter it. */
    function renderCaveats(rows) {
        const counts = new Map();
        rows.forEach((row) => {
            (row.warnings || []).forEach((w) => {
                // Group by the wording after the leading count, so "3 day(s)…"
                // and "5 day(s)…" collapse into one line naming the projects.
                const kind = w.replace(/^[\d.]+\s/, '');
                if (!counts.has(kind)) counts.set(kind, []);
                counts.get(kind).push(row.project);
            });
        });
        const notes = [...counts.entries()].map(([kind, projects]) => {
            const shown = projects.slice(0, 4).join(', ');
            const more = projects.length > 4 ? ` and ${projects.length - 4} more` : '';
            return `${projects.length} project(s) — ${kind} (${shown}${more})`;
        });
        caveatBox.style.display = notes.length ? '' : 'none';
        caveatText.textContent = notes.join(' · ');
    }

    function renderChart(computed) {
        const labels = computed.months.map(monthLabel);
        const staff = computed.months.map((m) => -Math.abs(computed.staff[m] || 0));
        const other = computed.months.map((m) => -Math.abs(computed.other[m] || 0));
        const revenue = computed.months.map((m) => computed.revenue[m] || 0);

        const anything = [...staff, ...other, ...revenue].some((v) => v !== 0);
        chartEmpty.style.display = anything ? 'none' : '';
        if (chart) { chart.destroy(); chart = null; }
        if (!anything) return;

        chart = new Chart(chartCanvas.getContext('2d'), {
            data: {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: 'Revenue (milestones)',
                        data: revenue,
                        backgroundColor: 'rgba(2, 152, 143, 0.75)',
                        stack: 'flow',
                        order: 3,
                    },
                    {
                        type: 'bar',
                        label: 'Staff cost',
                        data: staff,
                        backgroundColor: 'rgba(176, 42, 55, 0.75)',
                        stack: 'flow',
                        order: 3,
                    },
                    {
                        type: 'bar',
                        label: 'Other costs',
                        data: other,
                        backgroundColor: 'rgba(255, 128, 0, 0.7)',
                        stack: 'flow',
                        order: 3,
                    },
                    {
                        type: 'line',
                        label: 'Cumulative cash position',
                        data: computed.cumulative,
                        borderColor: '#2b3a55',
                        backgroundColor: '#2b3a55',
                        borderWidth: 2,
                        pointRadius: 2,
                        tension: 0.2,
                        order: 1,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    // Costs below the line and revenue above, so the shape of a
                    // month reads at a glance; one axis keeps the cumulative
                    // line comparable with the bars.
                    x: { stacked: true, grid: { display: false } },
                    y: {
                        stacked: true,
                        ticks: { callback: (v) => EUR.format(v) },
                        grid: { color: 'rgba(0,0,0,0.06)' },
                    },
                },
                plugins: {
                    legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => `${ctx.dataset.label}: ${fmtEur(Math.abs(ctx.parsed.y))}`,
                        },
                    },
                },
            },
        });
    }

    function refresh(rows) {
        const computed = series(rows);
        scopeLabel.textContent = rows.length === payload.rows.length
            ? `every project (${rows.length})`
            : `${rows.length} of ${payload.rows.length} projects`;
        renderIndicators(rows, computed);
        renderCaveats(rows);
        renderChart(computed);
    }

    // --- the table ----------------------------------------------------------

    const money = (v) => (Number.isFinite(v) ? EUR.format(v) : '—');

    const table = new DataTable('forecastSummaryTable', {
        data: payload.rows,
        pageSize: 25,
        sortable: true,
        exportable: true,
        responsive: 'scroll',
        persistState: true,
        stateKey: 'forecastSummaryTable_state',
        onFiltered: refresh,
        onRowClick: (row, event) => {
            if (event && (event.ctrlKey || event.metaKey)) {
                window.open(row.project_url, '_blank');
            } else {
                window.location.href = row.project_url;
            }
        },
        columns: [
            {
                key: 'project', label: 'Project', sortable: true,
                // A project whose figures are incomplete says so on its own row,
                // not only in the banner above.
                formatter: (v, row) => (row.warnings && row.warnings.length
                    ? `${esc(v)} <i class="fas fa-exclamation-triangle fs-warn" `
                      + `title="${esc(row.warnings.join(' '))}"></i>`
                    : esc(v)),
            },
            { key: 'organisation', label: 'Organisation', sortable: true },
            { key: 'business_unit', label: 'BU', sortable: true },
            { key: 'client', label: 'Client', sortable: true },
            { key: 'country', label: 'Country', sortable: true },
            { key: 'status', label: 'Status', sortable: true },
            { key: 'start', label: 'Start', sortable: true },
            { key: 'end', label: 'End', sortable: true },
            { key: 'budget', label: 'Budget', sortable: true, className: 'fs-num', formatter: money },
            { key: 'staff_cost', label: 'Staff cost', sortable: true, className: 'fs-num', formatter: money },
            { key: 'other_cost', label: 'Other cost', sortable: true, className: 'fs-num', formatter: money },
            { key: 'total_cost', label: 'Total cost', sortable: true, className: 'fs-num', formatter: money },
            {
                key: 'margin', label: 'Margin', sortable: true, className: 'fs-num',
                formatter: (v) => (Number.isFinite(v)
                    ? `<span class="${v < 0 ? 'text-danger fw-bold' : ''}">${EUR.format(v)}</span>`
                    : '—'),
            },
            {
                key: 'margin_pct', label: 'Margin %', sortable: true, className: 'fs-num',
                formatter: (v) => (Number.isFinite(v) ? `${v.toFixed(1)}%` : '—'),
            },
            { key: 'revenue', label: 'Scheduled revenue', sortable: true, className: 'fs-num', formatter: money },
        ],
        filters: [
            {
                key: 'search', type: 'text',
                searchFields: ['project', 'organisation', 'business_unit', 'business_unit_name',
                               'client', 'country', 'status'],
            },
            { key: 'organisation', type: 'multiselect' },
            { key: 'business_unit_name', type: 'multiselect' },
            { key: 'project', type: 'multiselect' },
            { key: 'country', type: 'multiselect' },
            { key: 'status', type: 'multiselect' },
        ],
    });

    // The actuals switch changes what the server values past months from, so it
    // is a reload rather than a client-side filter.
    const actualsToggle = document.getElementById('fsActualsToggle');
    if (actualsToggle) {
        actualsToggle.addEventListener('change', () => {
            const url = new URL(window.location.href);
            if (actualsToggle.checked) url.searchParams.set('actuals', '1');
            else url.searchParams.delete('actuals');
            window.location.href = url.toString();
        });
    }

    // DataTable fires onFiltered on its first render, but guard against a build
    // where it does not: an empty indicator strip would look like "no data".
    if (!kpiWrap.childElementCount) refresh(payload.rows);

    return table;
}
